import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { cloneDeep } from 'lodash'

import {
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Fab,
  Tooltip,
} from '@mui/material'

import { Add } from '@mui/icons-material'
import FormLabel from '@mui/material/FormLabel'
import { addKeyToObjectsArray } from 'utils/addUniqueKeyToObjects'
import { formatToCNPJ } from 'utils/masks'
import { cnpjValid } from 'utils/validators'
import { hasAnswerChanged } from 'utils/functions'
import { OPERATION_TYPES } from 'utils/constants/operationTypes'

import styles from './styles'
import AnswerCard from '../AnswerCard'
import QuestionButton from '../QuestionButton'

export default function QuestionEight(props) {
  const { questionData, saveResponses } = props
  const classes = styles()

  const initialData = {
    corporate_name: '',
    cnpj: '',
    created_platforms: '',
    role: '',
    developed_activities: '',
    will_keep_role: false,
  }

  const initialErrors = {
    corporate_name: false,
    cnpj: false,
    created_platforms: false,
    role: false,
    developed_activities: false,
  }

  const initializeFormState = (data) => {
    return {
      hasNonXPHomologatedRobots: data.items.length > 0,
      items: data.items,
    }
  }

  const [formState, setFormState] = useState(initializeFormState(questionData))

  const [activityData, setActivityData] = useState(initialData)

  const [activityErrors, setActivityErrors] = useState(initialErrors)

  const [loading, setLoading] = useState(false)

  const [edited, setEdited] = useState(false)
  const [saved, setSaved] = useState(questionData.has_answer)

  const handleFieldChange = (event) => {
    const key = event.target.id
    const value =
      key === 'cnpj' ? formatToCNPJ(event.target.value) : event.target.value
    if (key === 'cnpj' && value?.length >= 19) {
      return
    }
    const newActivityData = cloneDeep(activityData)
    newActivityData[key] = value
    setActivityData(newActivityData)
  }

  const handleRadioChange = (event) => {
    const newFormState = cloneDeep(formState)
    newFormState.hasNonXPHomologatedRobots = event.target.value === 'yes'
    setFormState(newFormState)
  }

  const handleContinueRadioChange = (event) => {
    const newActivityData = cloneDeep(activityData)
    newActivityData.will_keep_role = event.target.value === 'yes'
    setActivityData(newActivityData)
  }

  const hasRobots = () => {
    return !(formState.items.length === 0)
  }

  const validateAndSave = async (skipValidation = false) => {
    if (
      formState.items.length > 0 &&
      formState.hasNonXPHomologatedRobots === false
    ) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
      return
    }
    const newQuestionData = cloneDeep(questionData)
    if (
      !skipValidation &&
      formState.hasNonXPHomologatedRobots &&
      !hasRobots()
    ) {
      toast.error(
        'Ao selecionar "Sim" deve-se adicionar ao menos uma atividade',
      )
    } else {
      newQuestionData.has_answer = true
      newQuestionData.items = formState.items
      setLoading(true)
      await saveResponses(
        newQuestionData,
        'automation_activities',
        !saved ? OPERATION_TYPES.CREATE : OPERATION_TYPES.UPDATE,
      )
        .then(() => {
          setSaved(true)
          setEdited(false)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const validateRequiredFields = () => {
    const newActivityErrors = initialErrors

    Object.keys(initialErrors).forEach((key) => {
      if (key === 'cnpj' && activityData[key] !== '') {
        newActivityErrors[key] = !cnpjValid(activityData[key])
      }
      if (activityData[key] === '') {
        newActivityErrors[key] = true
      }
    })

    setActivityErrors(newActivityErrors)

    return !Object.keys(newActivityErrors).some((key) => newActivityErrors[key])
  }

  const handleValidateAndUpdateState = () => {
    if (validateRequiredFields()) {
      const newFormState = cloneDeep(formState)
      newFormState.items.push(activityData)
      setFormState(newFormState)
      setActivityData(initialData)
    } else {
      toast.warn(
        'Favor, verifique se todos os campos obrigatórios foram preenchidos corretamente',
      )
    }
  }

  const handleRemoveActivity = (position) => {
    const newFormState = cloneDeep(formState)
    newFormState.items = newFormState.items.filter(
      (activity, index) => index !== position,
    )
    setFormState(newFormState)
  }

  const buildActivity = (platformInfo) => {
    return (
      <React.Fragment>
        <p>
          <span>Razão social: </span>
          {platformInfo.corporate_name}
        </p>
        <p>
          <span>CNPJ: </span>
          {platformInfo.cnpj}
        </p>
        <p>
          <span>Plataformas Desenvolvidas: </span>
          {platformInfo.created_platforms}
        </p>
        <p>
          <span>Cargo: </span>
          {platformInfo.role}
        </p>
        <p>
          <span>Atividades desenvolvidas: </span>
          {platformInfo.developed_activities}
        </p>
        <p>
          <span>Continuará desempenhando atividade? </span>
          {platformInfo.will_keep_role ? 'Sim' : 'Não'}
        </p>
      </React.Fragment>
    )
  }

  useEffect(() => {
    const hasChanged =
      hasAnswerChanged(questionData.items, formState.items) ||
      formState.hasNonXPHomologatedRobots !== questionData.items.length > 0
    setEdited(hasChanged)
    if (
      formState.items.length > 0 &&
      formState.hasNonXPHomologatedRobots === false
    ) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
    }
  }, [formState])

  useEffect(() => {
    setSaved(questionData.has_answer)
    setFormState(initializeFormState(questionData))
  }, [questionData])

  useEffect(() => {
    if (hasAnswerChanged(questionData.items, formState.items))
      validateAndSave(true)
  }, [formState.items])

  return (
    <div className={classes.root}>
      <div className={classes.formContainer}>
        <h3>Questão 8</h3>
        <div className={classes.form}>
          <FormControl>
            <FormLabel>
              Exerce atividade de assessoria, desenvolvimentos, suporte e/ou
              instalação relacionado a robôs automatizados de execução de ordens
              não homologados na XP?{' '}
            </FormLabel>
            <RadioGroup
              row
              value={formState.hasNonXPHomologatedRobots ? 'yes' : 'no'}
              name="hasRelations"
              onChange={handleRadioChange}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Sim" />
              <FormControlLabel value="no" control={<Radio />} label="Não" />
            </RadioGroup>
          </FormControl>

          {formState.hasNonXPHomologatedRobots ? (
            <div className={classes.formFields}>
              <TextField
                id="corporate_name"
                label="Razão Social"
                onChange={handleFieldChange}
                required
                fullWidth
                value={activityData.corporate_name}
                error={activityErrors.corporate_name}
                size="small"
              />

              <TextField
                id="cnpj"
                label="CNPJ"
                onChange={handleFieldChange}
                required
                value={activityData.cnpj}
                fullWidth
                error={activityErrors.cnpj}
                size="small"
              />
              <TextField
                id="created_platforms"
                label="Plataformas desenvolvidas"
                onChange={handleFieldChange}
                required
                value={activityData.created_platforms}
                fullWidth
                error={activityErrors.created_platforms}
                size="small"
              />
              <TextField
                id="role"
                label="Cargo"
                onChange={handleFieldChange}
                required
                value={activityData.role}
                fullWidth
                error={activityErrors.role}
                size="small"
              />
              <TextField
                id="developed_activities"
                label="Atividades desenvolvidas"
                onChange={handleFieldChange}
                error={activityErrors.developed_activities}
                value={activityData.developed_activities}
                required
                fullWidth
                multiline
                size="small"
              />

              <FormControl className={classes.radioQuestion}>
                <FormLabel id="">
                  Após o credenciamento continuará exercendo essa função?
                </FormLabel>
                <RadioGroup
                  row
                  value={activityData.will_keep_role ? 'yes' : 'no'}
                  name="hasRelations"
                  onChange={handleContinueRadioChange}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Sim"
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio />}
                    label="Não"
                  />
                </RadioGroup>
              </FormControl>
              <div className={classes.fabContainer}>
                <Tooltip title="Adicionar atividade">
                  <Fab
                    onClick={handleValidateAndUpdateState}
                    disabled={formState?.developed_activities?.length === 10}
                    size="small"
                    color="primary"
                  >
                    <Add />
                  </Fab>
                </Tooltip>
              </div>
            </div>
          ) : null}
        </div>
        {formState?.items?.length > 0 ? (
          <div className={classes.answersContainer}>
            {addKeyToObjectsArray(formState.items)?.map((activity, index) => (
              <AnswerCard
                key={activity.key}
                index={index}
                handleRemove={handleRemoveActivity}
                mustRemove={formState.hasNonXPHomologatedRobots === false}
              >
                {buildActivity(activity)}
              </AnswerCard>
            ))}
          </div>
        ) : null}
      </div>
      <div className={classes.buttonsContainer}>
        <QuestionButton
          variant="contained"
          onClick={validateAndSave}
          loading={loading}
          saved={saved}
          edited={edited}
          disabled={
            (formState.hasNonXPHomologatedRobots &&
              formState.items.length === 0) ||
            (saved && !edited)
          }
        />
      </div>
    </div>
  )
}
